import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { getWithRetry } from "../lib/fetchWithRetry";
import { newUi } from "../lib/featureFlags";
import { recentDistinct } from "../lib/matchSuggestions";
import { useAuth } from "../context/AuthContext";
import { readStoredSort, sortStorageKey } from "../lib/perfSort";
// Issue #899, review round 3 F6 — the `?edit=` deep link's own state and
// URL-sync effects live in this hook now, not inline here (see its own
// header for why and how it coordinates with #910's `useLogFlowStep`).
import { useEditDeepLink } from "../hooks/useEditDeepLink";
import { useFinishPlannedMatch } from "../hooks/useFinishPlannedMatch";
// Issue #904 — a refresh (or a direct link) partway through the new
// log-a-match flow used to drop a flag-on player back on the shipped
// Performance Tracking list, with the flow in progress simply gone. See
// that hook's own header for the full derivation; the short version is
// `logStep` restores from `?step=` on mount and stays synced to it as the
// player moves through the flow, so a LATER refresh restores the LATEST
// step, not always step 1. Its own file, not inline here, for the same
// `max-lines-per-function` reason `hooks/useGroundsNearYou.js` is its own
// file — this budget-tracked function had no headroom for two more effects.
import { useLogFlowStep } from "../hooks/useLogFlowStep";
// Issue #1110 — usage counts for the same flow, from its step state alone.
import { useLogFlowUsage } from "../hooks/useLogFlowUsage";
// Issue #757 review round 2 (B1/B8) — the step's own page, extracted into
// its own component so this file keeps only the FLOW: which step is
// current, what has been entered, what carries forward. `LogGroundFlow`
// owns the rendering (the drawn navy `TopBar`, the step counter, which of
// the two sub-screens is showing) and the chromeless/text-size-retirement
// wiring for the duration it is mounted.
import LogGroundFlow from "../components/LogGroundFlow";
// Issue #758 — step 2's own page, the same extraction shape as
// `LogGroundFlow` above: this file supplies the flow (form state, which
// mode, the save call), `LogScorecardFlow` owns the rendering.
import LogScorecardFlow from "../components/LogScorecardFlow";
// Issue #759 — step 3's own page, same extraction shape as `LogGroundFlow`/
// `LogScorecardFlow` above: this file supplies the flow (the saved match,
// the fetched stats/goal, the reflection hand-off, skip-to-dashboard),
// `LogSavedFlow` owns the rendering.
import LogSavedFlow from "../components/LogSavedFlow";
// Issue #759 — extracted out of this file (issue #216's own component)
// so both the shipped inline call site below AND `LogSavedFlow`'s new one
// can reuse it — see that file's own header for the extraction's reasoning
// and the D57 fold-in (`suppressFailureToast`).
import ReflectionPrompt from "../components/ReflectionPrompt";
// Issue #757 review round 2, B8 — `MatchCard`/`StatCell` extracted
// unchanged (pure move), same reason as `LogGroundFlow` above.
import MatchCard from "../components/PerformanceMatchCard";
// Issue #1030 (D130, #726) — the redrawn phone list, behind `newUi("matches")`.
import MatchesScreen from "../components/MatchesScreen";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useCreateDialog } from "../hooks/useCreateDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import GroundPicker from "../components/GroundPicker";
import FacilityPicker from "../components/FacilityPicker";
import { useGroundFacilities } from "../components/FacilityPicker";
import InlineFacilityRating from "../components/InlineFacilityRating";
import { facilityAfterGroundChange, facilityOptionLabel, facilityStoredLabel } from "../lib/facilities";
import { draftFromOwnRating, facilityRatingPath, ratingBodyFromDraft, ratingDraftComplete } from "../lib/facilityRatings";
import { groundFieldError } from "../lib/groundRef";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";
import { Plus, Pencil, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { canSubmitRequired, useRequiredFields } from "../lib/requiredFields";
import { dateTimeLabel, formatClockTime } from "../lib/clockTime";
import { counterOrNull, counterText, rateOrNull } from "../lib/counters";
import { economyOrNull, oversRefusal } from "../lib/overs";

// Issue #142: human labels for the required-field keys the API can name
// (GET /matches/required-fields) — this is presentation only (what to
// call a field in an inline error), not the set of which fields ARE
// required. If the API ever requires a field this map doesn't know, the
// field key itself is used as a readable-enough fallback (see
// `FIELD_LABELS[key] || key` below) rather than silently dropping it.
const FIELD_LABELS = {
  opponent: "Opponent",
  competition: "Competition",
  ground: "Ground",
  // Issue #356: `ground_id` is what the API requires now — the RECORD, not
  // the name — so the required-fields list names it, and the user has to be
  // told about it as "Ground" rather than as a column name.
  ground_id: "Ground",
  match_date: "Date",
};

const DISMISSAL_TYPES = ["Not Out", "Bowled", "Caught", "LBW", "Run Out", "Stumped", "Hit Wicket", "Retired", "Obstructing Field"];

// Issue #127: the 18 counter fields below start blank, not `0` — a
// pre-filled numeric zero is what caused typing to append ("099") instead
// of replace, and made the field impossible to clear (see `toNum`/setters
// below for the other half of the fix). `batting_position` is a Select
// (not free text) and stays numeric; `dismissal_type`/`not_out` are not
// counters at all.
const empty = {
  // #356: `ground` (what the user sees) and `ground_id` (the record) are one
  // field in two halves — the picker sets and clears them together.
  // #546: `facility_id` is the third half-step down — WHICH field or net at
  // that ground. Null is "Not sure", a real answer and the starting one;
  // nothing is ever preselected.
  opponent: "", competition: "", season: "", ground: "", ground_id: null, facility_id: null, match_date: "", match_time: "", team: "", match_format: "T20",
  // Issue #704. `null` — never asked — the same starting value a fresh log
  // has always implied, now made explicit and saved. Top-level, alongside
  // `ground_id`/`match_time`, so it rides straight into `payload` below
  // with everything else `{ ...form }` already sends.
  kept_wicket: null,
  batting: { batting_position: 3, runs: "", balls_faced: "", fours: "", sixes: "", dismissal_type: "Not Out", not_out: true },
  bowling: { overs: "", maidens: "", runs_conceded: "", wickets: "", dot_balls: "", wides: "", no_balls: "" },
  fielding: { fielding_catches: "", wicketkeeping_catches: "", run_outs: "", stumpings: "", direct_hit_run_outs: "", assists: "", byes_conceded: "", wicketkeeping_run_outs: "", wicketkeeping_assists: "" },
};

// The counter keys affected by issue #127, grouped the same way `form` is —
// 18 at that ticket, 20 since #727 added the keeper's own run outs and
// assists. Issue #715 is what turns a blank one into a stored `null`
// instead of a `0`, and every key listed here takes that path.
const COUNTER_FIELDS = {
  batting: ["runs", "balls_faced", "fours", "sixes"],
  bowling: ["overs", "maidens", "runs_conceded", "wickets", "dot_balls", "wides", "no_balls"],
  fielding: ["fielding_catches", "wicketkeeping_catches", "run_outs", "stumpings", "direct_hit_run_outs", "assists", "byes_conceded", "wicketkeeping_run_outs", "wicketkeeping_assists"],
};

const FORMATS = ["T10", "T20", "T20I", "Limited Overs 30", "Limited Overs 40", "List A", "ODI", "First-Class", "Test"];

// Issue #106: default control height (h-9, 36px) is a mouse-sized target.
// Bump to ~44px on phones where this form is actually used (at the ground);
// hand back to the original density at `sm:` so desktop is unchanged.
const TOUCH = "h-11 sm:h-9";

// Parse free-text numeric input: strip anything except digits/decimal, coerce to number.
// Used at point-of-use (derived display values) and at submit — never per
// keystroke on the counter fields themselves (issue #127: coercing on every
// keystroke is what caused typing to append instead of replace).
function toNum(v) {
  if (v === "" || v === null || v === undefined) return 0;
  const cleaned = String(v).replace(/[^\d.\-]/g, "");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

// Issue #127: counter inputs hold the raw typed string while the user is
// editing — no coercion to a number, and no forcing back to `0`. Only
// strips characters that could never be part of a valid count (letters,
// symbols); "" passes through untouched so the field can be cleared.
// `toNum` (above) turns a value into a real number for the read-only
// derived displays and the batting-position Select. SUBMIT no longer goes
// through it: issue #715 routes every counter through `counterOrNull`
// (lib/counters.js) instead, because `toNum` answers `0` for a blank and
// that is the lie #715 exists to remove.
function sanitizeCounterInput(v) {
  if (v === "" || v === null || v === undefined) return "";
  return String(v).replace(/[^\d.]/g, "");
}

// Issue #127 (openEdit): build a form group (batting/bowling/fielding) from
// an API record, converting each counter to the string the input expects —
// null/undefined (never recorded) becomes "", any real number (including
// `0`) becomes its exact string form, so a recorded zero still shows as 0.
function toGroup(group, source) {
  const merged = { ...empty[group], ...(source || {}) };
  for (const k of COUNTER_FIELDS[group]) {
    const v = source?.[k];
    merged[k] = v === null || v === undefined ? "" : String(v);
  }
  return merged;
}

// Auto-suggest current + next few cricket seasons (Oct→Sep) alongside historical entries.
function _defaultSeasons() {
  const now = new Date();
  const year = now.getMonth() >= 9 ? now.getFullYear() : now.getFullYear() - 1;
  return [0, -1, -2, 1].map((delta) => {
    const y = year + delta;
    return `${y}/${String(y + 1).slice(-2)}`;
  });
}

export default function PerformanceTracking() {
  // Issue #719 half 1: THE ROUTER OWNS THE ROLE GATE. #74 put a coach
  // bounce in this component body because `/performance` carried no
  // `roles` array; App.js now states `roles={["player"]}` on the
  // route (#1166 took `admin` out: D215.2 sends an admin to /coach-portal),
  // the same way /statistics and /onboarding do, so no coach or admin ever
  // reaches this function and nothing here needs to know about roles.
  // #74's reasoning survives the move unchanged — a redirect rather than a
  // read-only variant, because one player's own match log is not the
  // coach-facing surface; the coach-facing one is
  // `pages/CoachPlayerRecord.jsx`, reached from the roster. Do not add a
  // second role test here: two gates on one page is how they drift.
  // Issue #1030 — the redrawn list (`newUi("matches")`, a player's).
  // Read first: the sort's default below needs it.
  // Not a role gate: the router still owns who reaches `/performance` (#719). This only
  // picks the body (the Settings.jsx:130
  // precedent), and `CoachPlayerRecord.test.js` names this exact line as its one exception.
  const { user } = useAuth();
  const matchesOn = newUi("matches") && user?.role === "player";
  const [matches, setMatches] = useState([]);
  // Issue #271: distinguishes "you have no matches" from "we don't know".
  const [loadFailed, setLoadFailed] = useState(false);
  // Issue #1030 — false until `GET /matches` has answered either way, so the
  // redrawn list can show its loading shapes instead of the empty sentence.
  const [loaded, setLoaded] = useState(false);
  // Issue #79: `?new=1` opens this dialog on arrival, so the Dashboard's
  // activity chooser lands a player IN the match form rather than on the
  // page beside its button. Same `[open, setOpen]` as the plain useState it
  // replaces — `closeDialog` below is unchanged.
  const [open, setOpen] = useCreateDialog();
  // Issue #904/#910 — `editingId` declared here, ahead of `useLogFlowStep`
  // below, purely so it can be PASSED to that hook (review round 3, F8):
  // the hook needs to know an edit is in progress before it decides
  // whether to touch `?step=` at all. Nothing else about this state
  // changed — same `useState(null)` it always was, just moved up.
  const [editingId, setEditingId] = useState(null);
  // Issue #757 — behind `newUi("log-ground")`, logging a NEW match opens on
  // step 1 ("Where did you play?") instead of the old single-scroll dialog.
  // Held in PAGE STATE, not a route (the issue's own §1: "three steps held
  // in page state, not three routes"). "ground" -> step 1's list of three
  // ways in; "add-ground" -> the reduced add-a-ground panel, replacing the
  // list IN THE SAME STEP; "form" -> the existing dialog content, unchanged
  // — steps 2/3 are sibling issues in this wave and are not built here, so
  // once a ground is resolved this slice hands off to the form that already
  // ships. EDITING an existing match never enters step 1 — only a fresh
  // "Log match" does (see `closeDialog`/`openEdit` below).
  //
  // Issue #904 — `useLogFlowStep` restores this from `?step=` on mount
  // (flag on only) and keeps the URL in sync as it changes, so a refresh
  // mid-flow lands back on the step the player was actually on, not
  // always "ground". See that hook's own header for the full derivation,
  // including review round 3's F6/F7/F8 fixes (the ordering bug, the
  // add-ground fallback, and never writing `?step=form` mid-edit).
  const [logStep, setLogStep, presetGround] = useLogFlowStep(open, setOpen, Boolean(editingId));
  const markLogSaved = useLogFlowUsage({ redrawn: newUi("log-ground"), open, editing: Boolean(editingId), logStep });
  const [form, setForm] = useState(empty);
  // #581 — the optional field/net rating captured alongside the match. Held
  // apart from `form` so it never rides into the match payload; the surface
  // is looked up from the venue's list so `ratingBodyFromDraft` knows which
  // axes exist (Turn & Grip on turf only). Reset whenever the surface or the
  // dialog changes — a verdict on one net must not carry to another.
  const [ratingDraft, setRatingDraft] = useState(() => draftFromOwnRating(null));
  const ratingFacilities = useGroundFacilities(form.ground_id);
  const selectedFacility = form.facility_id
    ? ratingFacilities.find((f) => f.facility_id === form.facility_id) || null
    : null;
  // Issue #142: the required-field set, fetched from the API rather than
  // hand-typed (see lib/requiredFields.js). `required.status` is loading,
  // known or unknown (issue #783); only "loading" disables Save.
  const required = useRequiredFields("/matches/required-fields");
  const [fieldErrors, setFieldErrors] = useState({});
  // #1299 (D304): the old dialog's Save takes the player to its first missing field.
  const dialogRef = useRef(null);
  const armFocus = useFocusFirstInvalid(dialogRef, fieldErrors);
  // Issue #711 — Competition, Your team and Opposition suggestions are built from `matches` by `recentDistinct` (below, next to `seasonOptions`).
  // Issue #758 — the on-screen save states DirectionA2.dc.html draws
  // (`saving`, `save-failed`): "idle" | "saving" | "failed". The old
  // dialog never tracked this (a toast was the only feedback); this state
  // powers only the new-UI screen's own banner/button, and toasting stays
  // for the flag-off leg.
  const [saveState, setSaveState] = useState("idle");
  // PR #841 review, B3 (D57: failures stay on the page, in the toast's own
  // words — toasts only ever confirm a success) — the new screen's own
  // banner shows the API's `detail`, not a fixed string; the old dialog
  // still gets the toast, unchanged.
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  // Issue #216: the just-logged match the reflection prompt is offering to
  // annotate, or null. Cleared by saving, skipping, or logging/editing
  // another match.
  //
  // Issue #376(a): also set by `openEdit` below, prefilled from
  // `reflectionsByMatch`. Declining the prompt once used to close the door
  // permanently — nothing re-offered it, because this was set ONLY by a
  // successful CREATE. A reflection is written after thinking about the
  // game, so "not now" is the ordinary answer, not a final one; the fix is
  // to offer the same box on the edit path too, pre-filled with whatever is
  // already saved (if anything) so "skip" and "update" land in the same
  // place.
  const [reflectFor, setReflectFor] = useState(null);
  // Issue #759 — step 3 ("the saved step")'s own data, held apart from
  // `form`/`matches`: the just-created match VERBATIM (the `POST /matches`
  // 201 body, fed to the echo — never a re-fetch, never form state, per
  // the issue's own §1), and the two fetch-on-save reads the rest of the
  // screen needs (`GET /stats/summary`'s `matches` count for the title/
  // holds sentence, `GET /players/me/identity`'s `vision.short_term_goal`
  // for the quoted-back goal card). Cleared on skip-to-dashboard.
  const [savedMatch, setSavedMatch] = useState(null);
  const [savedStats, setSavedStats] = useState(null);
  const [savedGoal, setSavedGoal] = useState("");
  const navigate = useNavigate();
  // Issue #899 — `editingViaDeepLink` (from `useEditDeepLink`, called below
  // once `openEdit` exists) skips the toast and returns to `/dashboard`;
  // see `create()`'s success branch and `closeDialog`.
  // Issue #376(a): match_id -> reflection, for prefilling `reflectFor` when
  // editing. Keyed by match rather than fetched per-click so opening Edit
  // is instant; refreshed after load and after any reflection save.
  const [reflectionsByMatch, setReflectionsByMatch] = useState({});
  const [q, setQ] = useState(() => localStorage.getItem("nw_perf_q") ?? "");
  const [comp, setComp] = useState(() => localStorage.getItem("nw_perf_comp") ?? "all");
  const [season, setSeason] = useState(() => localStorage.getItem("nw_perf_season") ?? "all");
  // #1030 review F3 — own storage key and default for the redrawn list (`lib/perfSort.js`).
  const [sort, setSort] = useState(() => readStoredSort(localStorage, matchesOn));

  useEffect(() => {
    try { localStorage.setItem(sortStorageKey(matchesOn), JSON.stringify(sort)); } catch { /* quota / private mode */ }
  }, [sort, matchesOn]);
  useEffect(() => { try { localStorage.setItem("nw_perf_q", q); } catch {} }, [q]);
  useEffect(() => { try { localStorage.setItem("nw_perf_comp", comp); } catch {} }, [comp]);
  useEffect(() => { try { localStorage.setItem("nw_perf_season", season); } catch {} }, [season]);

  // Issue #271: was an uncaught `.then()` chain — a failed GET /matches
  // left `matches` at `[]` and this page told a player who had just logged
  // an innings "No matches logged yet — your record starts with the first
  // one." This is the match HISTORY screen, so that sentence is the most
  // literal possible statement of "the app lost my data".
  const load = () =>
    getWithRetry("/matches")
      .then((r) => { setMatches(r.data || []); setLoadFailed(false); setLoaded(true); })
      .catch((e) => {
        setLoadFailed(true);
        setLoaded(true);
        console.error("PerformanceTracking: failed to load /matches", e);
      });

  // Issue #376(a): the player's own match reflections, indexed by match id.
  // A quiet best-effort load — a failure here must not block the match
  // history from rendering; it only means Edit opens the reflection box
  // blank instead of prefilled, same as the box a player who never wrote
  // one sees today.
  const loadReflections = () =>
    api
      .get("/reflections?kind=match")
      .then((r) => {
        const byMatch = {};
        for (const ref of r.data || []) byMatch[ref.match_id] = ref;
        setReflectionsByMatch(byMatch);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadReflections(); }, []);

  // Issue #142: was `if (!form.opponent || !form.match_date)` — a
  // hand-typed 2-of-4 check that let a missing competition/ground through
  // to a generic API 400. `required.fields` (fetched from the API's own
  // schema, see lib/requiredFields.js) drives this instead, so the set
  // checked here can't drift from what the API actually enforces.
  const validateRequired = () => {
    const errors = {};
    for (const key of required.fields) {
      const val = form[key];
      if (!val || !String(val).trim()) errors[key] = `${FIELD_LABELS[key] || key} is required`;
    }
    /**
     * Issue #356. `ground_id` being absent has two meanings and they need
     * different sentences: nothing typed at all ("Ground is required",
     * already produced above) versus a name typed but never resolved to a
     * record, which is the case #356 exists for and which the generic
     * message would describe as an empty field the user can plainly see is
     * not empty. `groundFieldError` owns that distinction.
     */
    const groundError = groundFieldError(form.ground, form.ground_id, { required: true });
    if (groundError) errors.ground_id = groundError;
    // #860 (D254 Q8): overs are overs and balls. 3.7 is refused here with the
    // words the API sends too, so the form and the server say the same thing.
    const ov = counterOrNull(form.bowling?.overs);
    if (ov != null && oversRefusal(ov)) errors.overs = oversRefusal(ov);
    return errors;
  };

  const create = async () => {
    // Belt-and-braces (issue #757 review round 2, B9; the race #782 already
    // fixed on `TrainingSessions.jsx`'s `ts-save`): `m-save` is `disabled`
    // only while the required-fields fetch is IN FLIGHT (see the Buttons),
    // so a real click can no longer land here before it's loaded. Kept as a
    // guard against a non-click caller rather than relied on as the fix, and
    // silent: a toast a fast clicker never gets to read is not a fix, it is
    // the bug wearing a message. Journey 1d (`e2e/journeys/01-...`) proves the
    // in-flight half with an injected 1.3s delay. Issue #783: when the fetch
    // FAILED the status is "unknown", Save is enabled, and the server's own
    // 400 answers (journey 8t).
    if (!canSubmitRequired(required.status)) return;
    const errors = validateRequired();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // PR #841 review, final round (D57) — the new screen's own summary
      // banner (`REQUIRED_SUMMARY`, `LogMatchScorecardStep.jsx`) already
      // carries these exact words on the page; a toast on top of it is the
      // same violation the save-failed branch below was already fixed for,
      // just on a different branch of the same function. The old dialog
      // has no such on-page summary, so its toast is unchanged.
      // #860: an overs error alone is not a missing field, so the old
      // dialog's toast says what is wrong instead of "required fields".
      if (!showLogScorecardStep) {
        const onlyOvers = Object.keys(errors).every((k) => k === "overs");
        toast.error(onlyOvers ? errors.overs : "Please fill in the required fields, highlighted below");
      }
      return;
    }
    setFieldErrors({});
    // Issue #758 — DirectionA2.dc.html's own "saving" state; reset on every
    // attempt so a retry after a failure clears the previous "failed" banner
    // the instant the click lands, not only once the new request resolves.
    setSaveState("saving");
    setSaveErrorMessage("");
    try {
      // Issue #127 put the raw typed string in form state so a counter
      // could be blank; issue #715 is what stops the blank being discarded
      // on the way out. `counterOrNull` sends `null` for an untouched
      // field and the real number for a touched one — `0` included, since a
      // recorded nought is an answer and only the UNTOUCHED field is
      // absent. The API's schema takes `null` on every one of these
      // (`z.number().nullable().optional()`), and every column behind them
      // is nullable but the two catches columns, which are NOT NULL
      // DEFAULT 0 from #36 and fold a null back to 0 server-side.
      const batting = { ...form.batting };
      for (const k of COUNTER_FIELDS.batting) batting[k] = counterOrNull(batting[k]);
      const bowling = { ...form.bowling };
      for (const k of COUNTER_FIELDS.bowling) bowling[k] = counterOrNull(bowling[k]);
      const fielding = { ...form.fielding };
      for (const k of COUNTER_FIELDS.fielding) fielding[k] = counterOrNull(fielding[k]);

      const payload = { ...form,
        batting: { ...batting,
          // #715, and the same rule one level up: a strike rate of 0 says
          // the player faced deliveries and scored off none. It must not
          // also be what an innings that never happened looks like, so no
          // balls faced means no strike rate.
          strike_rate: rateOrNull(batting.runs, batting.balls_faced, 100),
          not_out: form.batting.dismissal_type === "Not Out",
        },
        bowling: { ...bowling, economy_rate: economyOrNull(bowling.runs_conceded, bowling.overs) },
        fielding,
      };
      let saved;
      // Issue #1150 — Add how it went on a planned match: the same PUT, with
      // `status: "played"`, which turns the planned row into the record (#1148).
      const finishing = Boolean(finish.finishingId);
      if (editingId) {
        const res = await api.put(`/matches/${editingId}`, finishing ? { ...payload, status: "played" } : payload);
        saved = res.data;
        // Issue #899 — a deep-linked edit returns to `/dashboard`, where
        // the record itself shows the new value; a toast on top is the
        // double-confirmation `log-saved` already refuses for a create.
        // A finished planned match is confirmed on Plan ("Added to your record", D349).
        if (!editingViaDeepLink && !finishing) toast.success("Match updated");
      } else {
        const res = await api.post("/matches", payload);
        saved = res.data;
        markLogSaved();
        // PR #844 review, B2 — on the new-UI path this save hands off to
        // step 3 (the saved step) in place, and that whole screen IS the
        // confirmation (Saved.dc.html draws no toast at all); a toast on
        // top of it also landed physically over the Batting tiles at
        // arrival scroll position, doubling the confirmation and covering
        // real content. The old dialog (flag off) still closes and
        // reloads with no on-page confirmation of its own, so it keeps
        // the toast unchanged.
        if (!newUi("log-saved")) toast.success("Match logged!");
        // Issue #216: offer the reflection box AFTER the innings is safely
        // saved, never before. See ReflectionPrompt below for why this is an
        // inline card and not a dialog.
        setReflectFor({ match_id: res.data?.match_id, opponent: res.data?.opponent });
      }
      // #581 — the optional field/net rating rides AFTER the match row
      // exists, and off the SAVED row's ids: the played-here gate
      // (`userHasPlayedAtGround`) reads logged matches, so a rating PUT
      // before the POST 403s. A failed rating must not lose the saved match
      // — the match is already toasted as saved above, so this only adds a
      // second, non-fatal toast and the verdict can still be cast from
      // Grounds. On an edit the row already exists, so order is moot.
      if (saved?.ground_id && saved?.facility_id && selectedFacility && ratingDraftComplete(ratingDraft)) {
        try {
          await api.put(
            facilityRatingPath(saved.ground_id, saved.facility_id),
            ratingBodyFromDraft(ratingDraft, selectedFacility.surface),
          );
        } catch {
          toast.error("Match saved — rating not saved. You can rate this field or net from Grounds, or try again.");
        }
      }
      setSaveState("idle");

      // Issue #759 — a FRESH create (never an edit: the saved step reads
      // "your match is on the record", not "your edit was applied", and
      // brief-759.md's own scope is a create-time hand-off) behind
      // `newUi("log-saved")` moves to step 3 IN PAGE STATE instead of
      // closing the dialog and reloading — the old behaviour the issue's
      // own "Context" section names outright
      // (`PerformanceTracking.jsx:335` at the time it was filed: "closes
      // the dialog and reloads the page. The player is told nothing.").
      // `logStep` stays the same page-state machine #757/#758 already made
      // it; this adds a fourth value, not a route.
      // #1150, D349: a finished planned match does NOT go to the saved step. It
      // returns to Plan's Coming up, where "Added to your record" stands in the
      // card's place (the round-10 PlanMatchAfter board, "Saved" frame).
      if (finishing) {
        const done = { id: saved?.match_id ?? editingId, opponent: saved?.opponent ?? form.opponent };
        setReflectFor(null);
        closeDialog(false);
        navigate("/development", { state: { addedMatch: done } });
        return;
      }
      if (!editingId && newUi("log-saved")) {
        setSavedMatch(saved);
        // Both best-effort and fetched FRESH on every save (never cached
        // across saves, never read from `matches`/`form`): `GET
        // /stats/summary`'s own `matches` has to include the row just
        // written, and the goal card is a property of the SIGNED-IN
        // PLAYER, not of the match, so reading it once at mount would miss
        // a goal typed between two matches in the same session. A failed
        // read must not strand the player on a half-built screen when the
        // match itself is already safely on disk — falling back to `null`/
        // `""` renders the same "count omitted"/"not said yet" paths the
        // title and goal card already carry for a genuinely absent value,
        // never a fabricated one.
        const [statsRes, identityRes] = await Promise.allSettled([
          api.get("/stats/summary"),
          api.get("/players/me/identity"),
        ]);
        setSavedStats(statsRes.status === "fulfilled" ? statsRes.value.data : null);
        setSavedGoal(identityRes.status === "fulfilled" ? (identityRes.value.data?.vision?.short_term_goal || "") : "");
        setLogStep("saved");
        load();
      } else {
        // Issue #899 — `closeDialog`'s own `endDeepLinkEdit()` call handles
        // a deep-linked edit's return to `/dashboard`; a no-op otherwise.
        closeDialog(false); load();
      }
    } catch (e) {
      // PR #841 review, B3 (D57) — a failure stays ON THE PAGE, in the
      // banner, using the API's own `detail` when it has one. The new
      // screen raises NO toast at all: this repo's toasts are reserved for
      // confirming a success (every other `toast.success` call in this
      // file), and the old dialog's own toast-only pattern is what D57
      // is correcting, not extending. `showLogScorecardStep` is read here,
      // not passed in, because this same `create` also serves the
      // flag-off/editing-old-dialog path, which keeps its toast unchanged.
      // #1150 (D259): finishing a planned match with no reason from the API hands the
      // step NO message, so it says the board's sentence rather than "Failed to save match".
      const detail = e?.response?.data?.detail;
      const message = detail || (finish.finishingId ? "" : "Failed to save match");
      setSaveState("failed");
      setSaveErrorMessage(message);
      if (!showLogScorecardStep) toast.error(message || "Failed to save match");
    }
  };

  const openEdit = (m) => {
    setFieldErrors({});
    setSaveState("idle");
    setSaveErrorMessage("");
    setEditingId(m.match_id);
    // #581 — the inline rating opens empty on an edit (no prefill of a prior
    // verdict); the PUT is an upsert, so casting one here updates the single
    // rating row rather than adding a second. The map remains the place to
    // review an existing rating.
    setRatingDraft(draftFromOwnRating(null));
    // Issue #376(a): editing an already-logged match offers the reflection
    // box too, not just the moment it was created. Set here — when the
    // player opens Edit — rather than only after a successful save, so
    // reaching the reflection doesn't require resubmitting stats the
    // player didn't come here to change. Prefilled from whatever is
    // already saved for this match, so "skip" and "update" both land on
    // the same upserting `POST /reflections`
    // (`api/src/db/repos/reflections.ts`'s `upsertMatchReflection`).
    const existing = reflectionsByMatch[m.match_id];
    setReflectFor({ match_id: m.match_id, opponent: m.opponent, body: existing?.body || "" });
    setForm({
      opponent: m.opponent || "",
      competition: m.competition || "",
      season: m.season || "",
      ground: m.ground || "",
      /**
       * #356. `null` on a match logged before ground records existed. The
       * picker resolves the stored name against the directory as soon as it
       * loads, so a legacy match at a known ground opens ALREADY VALID and
       * the player is asked nothing — see `lib/groundRef.js`. Only a name
       * the directory does not know reaches the re-pick.
       */
      ground_id: m.ground_id ?? null,
      // #546. Null on every match that recorded no surface — including every
      // match logged before the column existed — and the picker's "Not sure".
      facility_id: m.facility_id ?? null,
      match_date: m.match_date || "",
      // #463. `""` for every match with no time — including every match
      // logged before the column existed — so the control opens blank.
      match_time: m.match_time || "",
      team: m.team || "",
      match_format: m.match_format || "T20",
      // #704. `?? null` — a match saved before this column existed, or
      // since without the tick touched, opens the edit form unticked
      // rather than defaulting to "answered no".
      kept_wicket: m.kept_wicket ?? null,
      venue: m.venue,
      // Issue #127: a stored `0` is a fact ("0 runs") and must show as `0`,
      // not blank — only a genuinely absent value (null/undefined, never
      // recorded) becomes "". `toGroup` stringifies each counter
      // individually rather than spreading `m.batting` etc. straight over
      // `empty`, since the raw API value is a number and the form now
      // expects a string.
      batting: toGroup("batting", m.batting),
      bowling: toGroup("bowling", m.bowling),
      fielding: toGroup("fielding", m.fielding),
    });
    // #757 — editing an already-logged match already HAS a ground; it skips
    // step 1 straight to the form that already ships (that form's own
    // ground field, `m-ground`, is exactly how the ground gets changed).
    setLogStep("form");
    setOpen(true);
  };

  // Issue #899, review round 3 F6 — `?edit=<matchId>`, extracted to
  // `useEditDeepLink.js` (see its own header). Called after `openEdit`,
  // which the hook needs. `endDeepLinkEdit` is a no-op unless a deep-linked
  // edit is actually in progress, so `closeDialog` below calls it
  // unconditionally on every close (Cancel, backdrop, Esc, or a save that
  // routes through here too).
  const [editingViaDeepLink, endDeepLinkEdit] = useEditDeepLink(matches, openEdit);
  // Issue #1150 — `?finish=<matchId>`: Add how it went opens a planned match's scorecard.
  // Both flags: the scorecard is `log-scorecard`'s, and the confirmation it ends on is
  // Plan's Coming up pill, which exists only on `dev-chrome` (the card is on that look too).
  const finish = useFinishPlannedMatch(openEdit, newUi("log-scorecard") && newUi("dev-chrome"));

  const closeDialog = (isOpen) => {
    setOpen(isOpen);
    setFieldErrors({});
    setSaveState("idle");
    setSaveErrorMessage("");
    if (!isOpen) {
      setEditingId(null);
      setForm(empty);
      setRatingDraft(draftFromOwnRating(null));
      // #757 — the next "Log match" starts back at step 1.
      setLogStep("ground");
      // Issue #899 — a no-op unless a deep-linked edit is actually live.
      endDeepLinkEdit();
      finish.clearFinish();
    }
  };

  // #757 review round 2, B1 — whether THIS open is showing step 1 (or its
  // add-a-ground panel) rather than the form that already ships. `&& open`
  // is now load-bearing (not merely implicit via nesting inside a
  // Radix `<Dialog>`, round 1's shape): `LogGroundFlow` renders as a top-
  // level page replacement, not inside any dialog, so nothing else gates
  // it from firing on every `/performance` visit. Editing never shows it
  // (`openEdit` above sets `logStep` to "form" up front), and once step 1
  // resolves a ground it hands off to the form too — steps 2/3 are sibling
  // issues, not built in this slice.
  // Issue #759 — `logStep` gained a fourth value ("saved") this issue adds;
  // this gate used to read simply `logStep !== "form"` (true of "ground"
  // AND "add-ground", the only two values that used to exist alongside
  // "form"), which silently also matched "saved" and reopened step 1 the
  // instant a fresh save moved past it. Excluded explicitly rather than
  // inverted to "=== ground || === add-ground": a fifth value added later
  // without updating this line fails the same way "saved" just did, so the
  // narrower fix is the one worth flagging in review, not the one that
  // quietly protects against a mistake nobody has made yet.
  const showLogGroundStep1 = newUi("log-ground") && open && !editingId && logStep !== "form" && logStep !== "saved";
  const [addGroundSeed, setAddGroundSeed] = useState("");

  // Issue #757 — any of the three ways in (or the map) resolves a real
  // record, exactly like the shipped picker's own `onChange(name,
  // groundId)` pair (`GroundPicker.jsx`'s module doc): both halves, always
  // together, never a name with no id behind it.
  const handleStep1Picked = (name, groundId) => {
    setForm((prev) => ({
      ...prev,
      ground: name,
      ground_id: groundId,
      facility_id: facilityAfterGroundChange(prev.ground_id, groundId, prev.facility_id),
    }));
    setRatingDraft(draftFromOwnRating(null));
    setLogStep("form");
  };

  const handleStep1AddGround = (seed) => {
    setAddGroundSeed(seed || "");
    setLogStep("add-ground");
  };

  // #757 §3 — `POST /grounds` (via `LogMatchAddGroundPanel`) returns the
  // full record, so the field is resolved the instant the panel saves —
  // same "add-first is the fastest path to a VALID field" shape
  // `GroundPicker.jsx`'s `handleSaved` already relies on.
  const handleAddGroundSaved = (ground) => handleStep1Picked(ground.name, ground.ground_id);

  // Issue #758 — whether THIS open is showing step 2, the scorecard,
  // rather than the old 31-input dialog. Covers BOTH paths that land on
  // `logStep === "form"`: a fresh log handed off from step 1 (#757), and
  // `openEdit` above, which sets `logStep` to "form" directly. `LogScorecardFlow`
  // tells the two apart via `editingId` (its own `mode` prop, below) —
  // same "no step counter, no ground step, Change is the only way to
  // change it" shape brief-758.md's own commentary states draw.
  const showLogScorecardStep = newUi("log-scorecard") && open && logStep === "form";

  // Issue #759 — step 3, the saved step: whether THIS open is showing it
  // rather than any other `logStep`. Only ever reached from `create()`'s
  // own fresh-create branch above — editing never sets `logStep` to
  // "saved" — so this is always "your match is on the record", never "your
  // edit was applied".
  const showLogSavedStep = newUi("log-saved") && open && logStep === "saved";

  // Issue #759 — "Skip it — see what the record holds" (`log-saved-skip`,
  // ruling 1: a text link, not a boxed SecondaryAction, but still reaching
  // `/dashboard`). Resets every piece of log-flow state the way `closeDialog`
  // already does on a plain close, plus the three step-3-only reads, so a
  // player who logs a second match starts step 1 clean rather than finding
  // the previous match's echo for a moment.
  const handleSkipToDashboard = () => {
    closeDialog(false); // same reset closing the shipped dialog already does
    setSavedMatch(null);
    setSavedStats(null);
    setSavedGoal("");
    setReflectFor(null);
    navigate("/dashboard");
  };

  // Issue #758 — a generic top-level field setter for the new screen (date,
  // competition, team, opponent, time, format, season): sets the value and
  // clears any existing error on that same key, mirroring what each field's
  // own onChange already did by hand in the old dialog.
  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((f) => (f[key] ? { ...f, [key]: undefined } : f));
  };

  // Issue #758 — "Back" (create mode) and "Change" both return to step 1
  // (D35, decisions-2026-09-24.md:190) without losing what's been entered:
  // `form` is untouched by this, only `logStep` moves.
  const handleReturnToStep1 = () => setLogStep("ground");

  // The shipped page's confirmation (flag off, and an admin on the new look,
  // who keeps the shipped body): #1124 made the dialog the one confirmation on
  // a player's new look; #1085 deletes this branch.
  const remove = async (m) => {
    if (!confirm("Delete match?")) return;
    await api.delete(`/matches/${m.match_id}`);
    load();
  };

  // Issue #1030 — the redrawn list confirms in its own in-app dialog
  // (`DeleteMatchDialog`), so it calls this with the confirmed match; a failure
  // rejects and stays on the dialog (D57). The shipped `remove` above keeps its
  // native `confirm()` for the shipped body alone (#1124).
  const deleteMatch = async (m) => {
    await api.delete(`/matches/${m.match_id}`);
    await load();
  };

  // Issue #127: `batting_position` is Select-driven (not free text), so it
  // still coerces to a number immediately. `dismissal_type` isn't numeric.
  // Every other key here is one of the 18 counters — hold the raw
  // (sanitized) string; `toNum` runs only at submit or for derived display.
  const setB = (k, v) => setForm({ ...form, batting: { ...form.batting, [k]: k === "dismissal_type" ? v : k === "batting_position" ? toNum(v) : sanitizeCounterInput(v) } });
  const setBw = (k, v) => {
    // #860: retyping overs ends its refusal at once (words, border, aria-invalid).
    if (k === "overs" && fieldErrors.overs) setFieldErrors(({ overs: _gone, ...rest }) => rest);
    setForm({ ...form, bowling: { ...form.bowling, [k]: sanitizeCounterInput(v) } });
  };
  const setF = (k, v) => setForm({ ...form, fielding: { ...form.fielding, [k]: sanitizeCounterInput(v) } });

  const competitions = ["all", ...Array.from(new Set(matches.map((m) => m.competition).filter(Boolean)))];
  const { competitionOptions, teamOptions, opponentOptions } = useMemo(() => ({
    competitionOptions: recentDistinct(matches, "competition"),
    teamOptions: recentDistinct(matches, "team"),
    opponentOptions: recentDistinct(matches, "opponent"),
  }), [matches]);
  const seasonOptions = Array.from(new Set([
    ...matches.map((m) => m.season).filter(Boolean),
    ..._defaultSeasons(),
  ])).sort().reverse();

  const filtered = matches.filter((m) => {
    const ql = q.toLowerCase();
    const okQ = !q ||
      m.opponent?.toLowerCase().includes(ql) ||
      m.ground?.toLowerCase().includes(ql) ||
      m.team?.toLowerCase().includes(ql) ||
      m.competition?.toLowerCase().includes(ql);
    const okC = comp === "all" || m.competition === comp;
    const okS = season === "all" || m.season === season;
    return okQ && okC && okS;
  });

  // ---------- Sortable columns ----------
  const SORT_ACCESSORS = {
    // #463. Date first, then the time on it, so two matches on the same day
    // no longer sort by nothing. A match with no time sorts before one with
    // a time on the same date, which is the only ordering that does not
    // claim to know something the row does not say.
    match_date: (m) => `${m.match_date || ""} ${formatClockTime(m.match_time)}`,
    competition: (m) => (m.competition || "").toLowerCase(),
    season: (m) => m.season || "",
    team: (m) => (m.team || "").toLowerCase(),
    opponent: (m) => (m.opponent || "").toLowerCase(),
    batting_position: (m) => m.batting?.batting_position ?? Infinity,
    runs: (m) => m.batting?.runs ?? -1,
    balls_faced: (m) => m.batting?.balls_faced ?? -1,
    fours: (m) => m.batting?.fours ?? -1,
    sixes: (m) => m.batting?.sixes ?? -1,
    strike_rate: (m) => m.batting?.strike_rate ?? -1,
    dismissal_type: (m) => (m.batting?.dismissal_type || "").toLowerCase(),
    wickets: (m) => m.bowling?.wickets ?? -1,
    economy_rate: (m) => m.bowling?.economy_rate ?? -1,
  };

  // Issue #110: same 14 keys as SORT_ACCESSORS (table headers), reused by the
  // mobile card list's "Sort by" select — keeps the sortable-column set a
  // single source of truth so mobile can never fall behind desktop.
  const SORT_COLUMNS = [
    { key: "match_date", label: "Date" },
    { key: "opponent", label: "Opponent" },
    { key: "competition", label: "Competition" },
    { key: "season", label: "Season" },
    { key: "team", label: "Team" },
    { key: "batting_position", label: "Batting position" },
    { key: "runs", label: "Runs" },
    { key: "balls_faced", label: "Balls faced" },
    { key: "fours", label: "Fours" },
    { key: "sixes", label: "Sixes" },
    { key: "strike_rate", label: "Strike rate" },
    { key: "dismissal_type", label: "Dismissal" },
    { key: "wickets", label: "Wickets" },
    { key: "economy_rate", label: "Economy rate" },
  ];

  const toggleSort = (key) => {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return { key: "match_date", dir: "asc" }; // third click resets to default
    });
  };

  // Issue #188: this list's empty state is search/filter-dependent, so the
  // two cases are not the same sentence — "you have logged nothing" and
  // "your filters exclude everything" read identically today and only one
  // of them is ever true. Same `filterActive` split Dashboard.jsx and
  // Statistics.jsx already make.
  const filterActive = !!q || comp !== "all" || season !== "all";
  // Issue #271: a third case, checked first — neither sentence below may be
  // said about a list that never arrived.
  const emptyLabel = loadFailed
    ? "Couldn't load your matches — nothing has been lost, we just couldn't reach the server."
    : filterActive
      ? "No matches match these filters."
      : "No matches logged yet — your record starts with the first one.";

  const sorted = [...filtered].sort((a, b) => {
    const acc = SORT_ACCESSORS[sort.key];
    if (!acc) return 0;
    const va = acc(a);
    const vb = acc(b);
    if (va < vb) return sort.dir === "asc" ? -1 : 1;
    if (va > vb) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const SortHead = ({ col, label, align = "left" }) => {
    const active = sort.key === col;
    const alignCls = align === "right" ? "justify-end text-right"
      : align === "center" ? "justify-center text-center" : "";
    const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
    return (
      <TableHead className={align === "right" ? "text-right" : align === "center" ? "text-center" : ""}>
        <button
          type="button"
          onClick={() => toggleSort(col)}
          className={`inline-flex items-center gap-1 font-semibold hover:text-[#0B2E59] transition ${active ? "text-[#0B2E59]" : "text-slate-600"} ${alignCls}`}
          data-testid={`perf-sort-${col}`}
          title={`Sort by ${label}`}
        >
          <span>{label}</span>
          <Icon size={12} className={active ? "opacity-100" : "opacity-40"} />
        </button>
      </TableHead>
    );
  };

  // Issue #757 review round 2, B1 — step 1 (and its add-a-ground panel)
  // is its OWN page surface (#803's decision), never nested inside the
  // shipped `Dialog`: nothing of the shipped page (this component's own
  // title, filters, table, the "Log match" trigger itself) renders while
  // it is up. `LogGroundFlow` owns the chromeless/text-size wiring for
  // exactly its own mount/unmount — see that component's own header.
  if (showLogGroundStep1) {
    return (
      <LogGroundFlow
        logStep={logStep}
        addGroundSeed={addGroundSeed}
        presetGround={presetGround}
        onPicked={handleStep1Picked}
        onAddGround={handleStep1AddGround}
        onAddGroundSaved={handleAddGroundSaved}
        onCancelAddGround={() => setLogStep("ground")}
        // Issue #757 review round 2, R1 (Andreas's decision, 2026-09-24) —
        // the step's own "Back" link, one level up from the add panel's
        // existing "Back to the list": leaves the flow entirely, the same
        // way closing the shipped dialog always has — `closeDialog(false)`
        // clears `editingId`/`form`/`logStep` back to "ground" so the next
        // "Log match" starts fresh, exactly as the old close-X did.
        onExitStep1={() => closeDialog(false)}
      />
    );
  }

  // Issue #758 — step 2, the scorecard, is its own page surface too (same
  // shape as step 1 above): nothing of the shipped page renders while it
  // is up. `mode` tells `LogScorecardFlow` whether this is a fresh log
  // (step counter, "Back"/"Change" both return to step 1) or an edit
  // (no step counter, no step 1 to return to — "Change" opens the ground
  // picker inline and "Back" closes the editor).
  if (showLogScorecardStep) {
    return (
      <LogScorecardFlow
        mode={finish.finishingId ? "finish" : editingId ? "edit" : "create"}
        form={form}
        setForm={setForm}
        setB={setB}
        setBw={setBw}
        setF={setF}
        updateField={updateField}
        fieldErrors={fieldErrors}
        requiredStatus={required.status}
        competitionOptions={competitionOptions}
        teamOptions={teamOptions}
        opponentOptions={opponentOptions}
        seasonOptions={seasonOptions}
        onBack={finish.finishingId ? () => { closeDialog(false); finish.leaveFinish(); } : editingId ? () => closeDialog(false) : handleReturnToStep1}
        onChangeGround={editingId ? null : handleReturnToStep1}
        onSave={create}
        saveState={saveState}
        saveErrorMessage={saveErrorMessage}
        // Issue #1028 — the stored surface's own label, for the venue's
        // list-unreadable/editing states; `facilityStoredLabel`
        // (`lib/facilities.js`) is the one-line extraction of the same
        // computation the old dialog's own `FacilityPicker` call still
        // does inline a few hundred lines down, kept OUT of this already
        // over-budget function (`max-lines-per-function`, #1028's own
        // stop-and-ask) rather than repeating it here.
        storedLabel={facilityStoredLabel(editingId ? matches.find((x) => x.match_id === editingId) || finish.finishMatch : null)}
        // Issue #1008 (D203.1, PR #1025 review F1) — the "Change ground"
        // picker's own add dialog hides Indoor here, the same gate that
        // already decided this whole branch renders. `LogScorecardFlow`
        // forwards it through `{...props}` (unchanged) to
        // `LogMatchScorecardStep`, which threads it to its own
        // `GroundPicker`. `PerformanceTracking.jsx:994`'s OWN legacy
        // `GroundPicker` (the old 31-input dialog) is untouched — no prop
        // passed there, so it stays on the default `hideIndoor = false`.
        hideIndoor={newUi("log-scorecard")}
      />
    );
  }

  // Issue #759 — step 3, the saved step, is its own page surface too (same
  // shape as steps 1 and 2 above): nothing of the shipped page renders
  // while it is up. `reflectFor`/`onReflectionDone` thread the SAME
  // `ReflectionPrompt` (#216, extracted for this reuse — see that
  // component's own header) that the shipped page renders inline below;
  // `LogMatchSavedStep` decides when it is offered vs. open.
  if (showLogSavedStep) {
    return (
      <LogSavedFlow
        match={savedMatch}
        facilityKind={selectedFacility?.kind}
        matchesCount={savedStats?.matches}
        seasonGoal={savedGoal}
        reflectFor={reflectFor}
        onReflectionDone={() => { setReflectFor(null); loadReflections(); }}
        onSkip={handleSkipToDashboard}
      />
    );
  }

  // Issue #216: the optional reflection prompt, shown after a match has
  // been saved (create) — issue #376(a) also shows it after opening
  // Edit, see `openEdit` above. `key` forces a remount when the target
  // match changes (create -> immediately edit a different match, or
  // edit A then edit B) so the prefilled body from A can never survive
  // into B's box.
  // Issue #1030 — the reflection prompt below, held in a variable so the
  // redrawn list (`newUi("matches")`) can carry the same element the
  // shipped page renders; unchanged otherwise.
  // Issue #1150 — Add how it went whose planned match could not be read: said on
  // the page, in place (D57), never silently the plain list.
  const finishNotice = finish.finishFailed && (
    <div role="alert" data-testid="finish-load-failed" className="nw-help">
      Couldn't open that match to add how it went. Nothing has been lost. Go back and try again.
    </div>
  );
  const reflectionPrompt = reflectFor?.match_id && (
    <ReflectionPrompt
      key={reflectFor.match_id}
      match={reflectFor}
      onDone={() => { setReflectFor(null); loadReflections(); }}
    />
  );

  // Issue #1030 — the desktop table below, held in a variable so the redrawn list
  // (`newUi("matches")`) can carry the same unchanged table at `md:` and up (the
  // redraw is phone-only, decision 19); the shipped page renders it in the same place.
  // Issue #1124 — a function of the row's delete handler: the flag-off page passes
  // `remove` (native confirm, deleted with #1085; the shipped body is also an
  // admin's on the new look); the redrawn list, a player's, passes the dialog's
  // opener, so no player on the new look meets a native confirm.
  const renderDesktopTable = (onDeleteRow) => (
    <Card className="hidden md:block border-slate-200">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table data-testid="matches-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10"></TableHead>
                <SortHead col="match_date" label="Date" />
                <SortHead col="competition" label="Competition" />
                <SortHead col="season" label="Season" />
                <SortHead col="team" label="Team" />
                <SortHead col="opponent" label="Opponent" />
                <SortHead col="batting_position" label="Pos" align="center" />
                <SortHead col="runs" label="R" align="right" />
                <SortHead col="balls_faced" label="B" align="right" />
                <SortHead col="fours" label="4s" align="right" />
                <SortHead col="sixes" label="6s" align="right" />
                <SortHead col="strike_rate" label="SR" align="right" />
                <SortHead col="dismissal_type" label="Dismissal" />
                <SortHead col="wickets" label="Wkts" align="right" />
                <SortHead col="economy_rate" label="Econ" align="right" />
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 && <TableRow><TableCell colSpan={16} className="text-center text-slate-500 py-8">{emptyLabel}</TableCell></TableRow>}
              {sorted.map((m) => (
                <TableRow key={m.match_id} className="hover:bg-slate-50">
                  <TableCell className="pr-0">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-${m.match_id}`} title="Edit match">
                      <Pencil size={14} />
                    </Button>
                  </TableCell>
                  {/* #463. Same "date · time" as the mobile card and the
                      training-session card — a time is shown wherever the
                      date is, or the feature is only half-delivered. */}
                  <TableCell className="text-sm whitespace-nowrap">{dateTimeLabel(m.match_date, m.match_time)}</TableCell>
                  <TableCell className="text-xs text-slate-600">{m.competition}</TableCell>
                  <TableCell className="text-xs text-slate-600 whitespace-nowrap">{m.season || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-600">{m.team || "—"}</TableCell>
                  <TableCell className="font-semibold text-slate-900">{m.opponent}</TableCell>
                  {/* #715. Every counter cell reads through `counterText`,
                      which prints a recorded `0` and a dash for a counter
                      that was never entered. The old `|| 0` could not tell
                      those apart, so a match the player did not bowl in
                      showed the same `0` as a wicketless spell — and the
                      `*` on runs only belongs to an innings that happened.
                      The dash is the mark the Season, Team, Pos and
                      Dismissal cells beside it already use. */}
                  <TableCell className="text-center text-sm">{counterText(m.batting?.batting_position)}</TableCell>
                  <TableCell className="text-right font-semibold text-[#0B2E59]">
                    {counterText(m.batting?.runs)}{m.batting?.runs != null && m.batting?.not_out ? "*" : ""}
                  </TableCell>
                  <TableCell className="text-right">{counterText(m.batting?.balls_faced)}</TableCell>
                  <TableCell className="text-right">{counterText(m.batting?.fours)}</TableCell>
                  <TableCell className="text-right">{counterText(m.batting?.sixes)}</TableCell>
                  <TableCell className="text-right">{counterText(m.batting?.strike_rate)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{m.batting?.dismissal_type || "—"}</TableCell>
                  {/* #1030 — gold is the action colour only (R-003): with the redrawn list on,
                    the wickets figure reads in ink, as `MatchListFold.dc.html` draws it. */}
                  <TableCell className={`text-right font-semibold ${matchesOn ? "text-slate-900" : "text-[#D4A017]"}`}>{counterText(m.bowling?.wickets)}</TableCell>
                  <TableCell className="text-right">{counterText(m.bowling?.economy_rate)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" onClick={() => onDeleteRow(m)} data-testid={`del-${m.match_id}`} title="Delete"><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  // Issue #1030 — the shipped log dialog, hoisted unchanged into a variable so
  // the redrawn list (`newUi("matches")`) can mount it too: with `log-ground`
  // or `log-scorecard` off, "Log a match" and Edit still open THIS dialog, and
  // the new page has no body of its own to hold it. The shipped page renders it
  // in the same place with the same trigger; only `matchesOn` drops the trigger
  // (the redrawn screen has its own gold `perf-log-btn`).
  const logDialog = (
    <Dialog open={open} onOpenChange={closeDialog}>
      {!matchesOn && (
        <DialogTrigger asChild>
          <Button data-testid="perf-log-btn" style={{ background: "#0B2E59" }}><Plus size={16} className="mr-2" />Log match</Button>
        </DialogTrigger>
      )}
      <DialogContent ref={dialogRef} className="max-w-2xl">
        <DialogHeader><DialogTitle>{editingId ? "Update match performance" : "Log a match performance"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Date{required.fields.includes("match_date") && " *"}</Label>
            <Input
              data-testid="m-date" type="date"
              className={`${TOUCH} ${fieldErrors.match_date ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              aria-invalid={fieldErrors.match_date ? true : undefined}
              aria-describedby={fieldErrors.match_date ? "m-date-error" : undefined}
              value={form.match_date}
              onChange={(e) => { setForm({...form, match_date: e.target.value}); setFieldErrors((f) => ({ ...f, match_date: undefined })); }}
            />
            {fieldErrors.match_date && <p className="text-xs text-red-600 mt-1" id="m-date-error" data-testid="m-date-error">{fieldErrors.match_date}</p>}
          </div>
          {/* #463 — "add scheduled game time with the date of the game".
              OPTIONAL and labelled so: most matches are logged after the
              fact, where a start time is rarely worth typing, and every
              match already logged has none. Not in the required list
              (the API's schema marks it optional and this form reads
              that list, #142), so no `*` can appear here. */}
          <div>
            <Label>Start time <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input
              data-testid="m-time" type="time"
              className={TOUCH}
              value={form.match_time}
              onChange={(e) => setForm({...form, match_time: e.target.value})}
            />
          </div>
          <div>
            <Label>Format</Label>
            <Select value={form.match_format} onValueChange={(v) => setForm({...form, match_format: v})}>
              <SelectTrigger data-testid="m-format" className={TOUCH}><SelectValue /></SelectTrigger>
              <SelectContent>{FORMATS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Competition{required.fields.includes("competition") && " *"}</Label>
            <Input
              data-testid="m-comp"
              className={`${TOUCH} ${fieldErrors.competition ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              aria-invalid={fieldErrors.competition ? true : undefined}
              aria-describedby={fieldErrors.competition ? "m-comp-error" : undefined}
              value={form.competition}
              onChange={(e) => { setForm({...form, competition: e.target.value}); setFieldErrors((f) => ({ ...f, competition: undefined })); }}
              placeholder="e.g. Thornmere Shield"
            />
            {fieldErrors.competition && <p className="text-xs text-red-600 mt-1" id="m-comp-error" data-testid="m-comp-error">{fieldErrors.competition}</p>}
          </div>
          <div>
            <Label>Season</Label>
            <Input
              data-testid="m-season"
              className={TOUCH}
              list="perf-season-suggestions"
              value={form.season}
              onChange={(e) => setForm({ ...form, season: e.target.value })}
              placeholder="e.g. 2025/26 (auto if blank)"
            />
            <datalist id="perf-season-suggestions">
              {seasonOptions.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div><Label>Your team</Label><Input data-testid="m-team" className={TOUCH} value={form.team} onChange={(e) => setForm({...form, team: e.target.value})} placeholder="e.g. Linnmere Sparks" /></div>
          <div>
            <Label>Opponent{required.fields.includes("opponent") && " *"}</Label>
            <Input
              data-testid="m-opp"
              className={`${TOUCH} ${fieldErrors.opponent ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              aria-invalid={fieldErrors.opponent ? true : undefined}
              aria-describedby={fieldErrors.opponent ? "m-opp-error" : undefined}
              value={form.opponent}
              onChange={(e) => { setForm({...form, opponent: e.target.value}); setFieldErrors((f) => ({ ...f, opponent: undefined })); }}
            />
            {fieldErrors.opponent && <p className="text-xs text-red-600 mt-1" id="m-opp-error" data-testid="m-opp-error">{fieldErrors.opponent}</p>}
          </div>
          <div>
            <Label>Ground{required.fields.includes("ground_id") && " *"}</Label>
            <GroundPicker
              inputTestId="m-ground"
              testIdPrefix="perf-ground"
              className={`${TOUCH} ${fieldErrors.ground_id ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              ariaInvalid={fieldErrors.ground_id ? true : undefined}
              ariaDescribedBy={fieldErrors.ground_id ? "m-ground-error" : undefined}
              openOnProgrammaticFocus={false}
              value={form.ground}
              groundId={form.ground_id}
              onChange={(name, groundId) => {
                setForm((prev) => ({
                  ...prev,
                  ground: name,
                  ground_id: groundId,
                  // #546, the pair rule in the UI (`TrainingSessions.jsx`'s
                  // move exactly): change the venue and the surface you
                  // picked at the old one is not there — the picker below
                  // re-populates from the new ground with nothing selected.
                  facility_id: facilityAfterGroundChange(prev.ground_id, groundId, prev.facility_id),
                }));
                setFieldErrors((f) => ({ ...f, ground_id: undefined }));
                // #581 — the surface cleared with the venue; its rating goes too.
                setRatingDraft(draftFromOwnRating(null));
              }}
              placeholder="e.g. Thornmere Park"
            />
            {fieldErrors.ground_id && <p className="text-xs text-red-600 mt-1" id="m-ground-error" data-testid="m-ground-error">{fieldErrors.ground_id}</p>}
            {/* #546 — "Which field or net?" Renders NOTHING until a ground
                is picked, and nothing at all for a ground with no recorded
                surfaces — the common case, which must look exactly as it
                did before. */}
            <FacilityPicker
              testId="m-facility"
              groundId={form.ground_id}
              value={form.facility_id}
              storedLabel={(() => {
                const m = editingId ? matches.find((x) => x.match_id === editingId) : null;
                return m?.facility_name ? facilityOptionLabel({ name: m.facility_name, surface: m.facility_surface }) : null;
              })()}
              onChange={(facilityId) => {
                setForm((prev) => ({ ...prev, facility_id: facilityId }));
                // #581 — a new surface is a new thing to rate; drop the
                // verdict cast against the last one.
                setRatingDraft(draftFromOwnRating(null));
              }}
            />
            {/* #581 — rate the picked field/net without leaving the form.
                Renders only when a specific surface is selected; "Not sure"
                or a ground with no surfaces shows nothing. Saved with the
                match (POST match → PUT rating), never before the row it
                needs exists. */}
            <InlineFacilityRating
              facility={selectedFacility}
              draft={ratingDraft}
              setDraft={setRatingDraft}
              savedWith="match"
              testId="m-facility-rating"
            />
          </div>
        </div>
        <Tabs defaultValue="batting" className="mt-4">
          <TabsList>
            <TabsTrigger value="batting" data-testid="tab-batting">Batting</TabsTrigger>
            <TabsTrigger value="bowling" data-testid="tab-bowling">Bowling</TabsTrigger>
            <TabsTrigger value="fielding" data-testid="tab-fielding">Fielding</TabsTrigger>
          </TabsList>

          <TabsContent value="batting" className="pt-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <Label>Batting position</Label>
                <Select value={String(form.batting.batting_position)} onValueChange={(v) => setB("batting_position", v)}>
                  <SelectTrigger data-testid="m-bat-pos" className={TOUCH}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({length: 11}, (_, i) => i + 1).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Runs</Label><Input data-testid="m-runs" type="number" inputMode="numeric" className={TOUCH} min={0} value={form.batting.runs} onChange={(e) => setB("runs", e.target.value)} /></div>
              <div><Label>Balls faced</Label><Input data-testid="m-balls" type="number" inputMode="numeric" className={TOUCH} min={0} value={form.batting.balls_faced} onChange={(e) => setB("balls_faced", e.target.value)} /></div>
              <div><Label>Fours</Label><Input data-testid="m-fours" type="number" inputMode="numeric" className={TOUCH} min={0} value={form.batting.fours} onChange={(e) => setB("fours", e.target.value)} /></div>
              <div><Label>Sixes</Label><Input data-testid="m-sixes" type="number" inputMode="numeric" className={TOUCH} min={0} value={form.batting.sixes} onChange={(e) => setB("sixes", e.target.value)} /></div>
              <div>
                <Label>Strike rate</Label>
                {/* Issue #127: form.batting.{runs,balls_faced} are now raw
                    strings (so the field can be blank) — the string "0"
                    is truthy in JS, so the divide-by-zero guard below
                    must read through toNum, not the raw value. */}
                <Input readOnly className={`bg-slate-50 ${TOUCH}`} value={toNum(form.batting.balls_faced) ? (toNum(form.batting.runs) / toNum(form.batting.balls_faced) * 100).toFixed(2) : "—"} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Dismissal type</Label>
                <Select value={form.batting.dismissal_type} onValueChange={(v) => setForm({...form, batting: { ...form.batting, dismissal_type: v, not_out: v === "Not Out" } })}>
                  <SelectTrigger data-testid="m-dismissal" className={TOUCH}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISMISSAL_TYPES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 py-2 -my-2">
                <Checkbox
                  id="not-out"
                  className="h-5 w-5"
                  checked={form.batting.dismissal_type === "Not Out" || form.batting.not_out}
                  onCheckedChange={(v) => setForm({...form, batting: { ...form.batting, not_out: !!v, dismissal_type: v ? "Not Out" : form.batting.dismissal_type }})}
                  data-testid="m-not-out"
                />
                <Label htmlFor="not-out" className="cursor-pointer py-2 -my-2">Not out</Label>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bowling" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 pt-4">
            {[
              ["overs","Overs"],
              ["maidens","Maidens"],
              ["runs_conceded","Runs conceded"],
              ["wickets","Wickets"],
              ["dot_balls","Dot balls"],
              ["wides","Wides"],
              ["no_balls","No balls"],
            ].map(([k,label]) => (
              <div key={k}><Label>{label}</Label>
                <Input data-testid={`m-${k}`} type="text" inputMode="decimal" className={TOUCH} value={form.bowling[k]} onChange={(e) => setBw(k, e.target.value)} />
              </div>
            ))}
            <div>
              <Label>Economy</Label>
              <Input readOnly className={`bg-slate-50 ${TOUCH}`} value={economyOrNull(form.bowling.runs_conceded, form.bowling.overs)?.toFixed(2) ?? "—"} />
            </div>
          </TabsContent>

          <TabsContent value="fielding" className="pt-4 space-y-4">
            {/* Issue #704, D180/(a) — the same tick the new scorecard
                carries (`LogMatchScorecardStep.jsx`'s `m-kept-wicket`),
                at the head of this group on the legacy form too, since
                real users still log matches here until `log-scorecard`
                is on for them (#842). Gates nothing on this form — every
                counter below stays visible either way, unlike the new
                scorecard's "Behind the stumps" reveal; this is the only
                place `form.kept_wicket` is set for most matches. */}
            <div className="flex items-center gap-3 py-2 -my-2">
              <Checkbox
                id="kept-wicket"
                className="h-5 w-5"
                checked={form.kept_wicket === true}
                onCheckedChange={(v) => setForm({ ...form, kept_wicket: !!v })}
                data-testid="m-kept-wicket"
              />
              <Label htmlFor="kept-wicket" className="cursor-pointer">I was the wicketkeeper</Label>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Fielding</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ["fielding_catches","Catches"],
                  ["run_outs","Run outs"],
                  ["direct_hit_run_outs","Direct hits"],
                  ["assists","Assists"],
                ].map(([k,label]) => (
                  <div key={k}><Label>{label}</Label>
                    <Input data-testid={`m-${k}`} type="text" inputMode="numeric" className={TOUCH} value={form.fielding[k]} onChange={(e) => setF(k, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
            {/* #727, Martin 2026-09-18 ("Separate them."): Run outs and
                Assists now appear on BOTH blocks and are different
                counters. A dismissal you effected at the stumps belongs
                here; one you effected from the covers belongs above. The
                shared column that could not tell them apart is the
                defect. Direct hits stay fielder-only — a keeper standing
                up is not throwing at the stumps they are behind. */}
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Wicket-keeping</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  ["wicketkeeping_catches","Catches (wk)"],
                  ["stumpings","Stumpings"],
                  ["byes_conceded","Byes conceded"],
                  ["wicketkeeping_run_outs","Run outs (wk)"],
                  ["wicketkeeping_assists","Assists (wk)"],
                ].map(([k,label]) => (
                  <div key={k}><Label>{label}</Label>
                    <Input data-testid={`m-wk-${k}`} type="text" inputMode="numeric" className={TOUCH} value={form.fielding[k]} onChange={(e) => setF(k, e.target.value)} />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1.5">Catches, run outs and assists are tracked separately for fielding and wicket-keeping. Leave a box blank if it did not happen. The two Catches boxes are the exception — they record a blank as 0.</p>
            </div>
          </TabsContent>
        </Tabs>
        <Button onClick={() => { armFocus(); create(); }} disabled={!canSubmitRequired(required.status)} className={`w-full mt-4 ${TOUCH}`} style={{ background: "#0B2E59" }} data-testid="m-save">
          {editingId ? "Update match" : "Save match"}
        </Button>
      </DialogContent>
    </Dialog>
  );

  // Issue #1030 (D130, #726) — the redrawn phone list, a player only. Placed
  // AFTER the three log-flow early returns above, so a flow in progress still
  // replaces the page, and BEFORE the shipped body so flag-off never reaches it.
  if (matchesOn) {
    return (
      <MatchesScreen
        matches={matches} sorted={sorted} loaded={loaded} loadFailed={loadFailed}
        q={q} setQ={setQ} comp={comp} setComp={setComp} season={season} setSeason={setSeason}
        competitions={competitions} seasonOptions={seasonOptions}
        sort={sort} setSort={setSort} sortColumns={SORT_COLUMNS}
        desktopTable={sorted.length > 0 ? renderDesktopTable : null}
        onLog={() => closeDialog(true)} onEdit={openEdit} onDelete={deleteMatch}
        onRetry={() => { setLoaded(false); setLoadFailed(false); load(); }}
        onClearFilters={() => { setQ(""); setComp("all"); setSeason("all"); }}
      >
        {finishNotice}
        {reflectionPrompt}
        {logDialog}
      </MatchesScreen>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Lifelong record</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-1">Performance Tracking</h1>
        </div>
        {logDialog}
      </div>

      {finishNotice}
      {reflectionPrompt}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative w-full sm:flex-1 sm:min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="perf-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search opponent, team, ground…" className="pl-9 h-10" />
        </div>
        <Select value={comp} onValueChange={setComp}>
          <SelectTrigger className="w-56" data-testid="perf-filter-comp"><SelectValue /></SelectTrigger>
          <SelectContent>
            {competitions.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All competitions" : c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={season} onValueChange={setSeason}>
          <SelectTrigger className="w-44" data-testid="perf-filter-season"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All seasons</SelectItem>
            {seasonOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-sm text-slate-500 ml-auto">{filtered.length} match{filtered.length !== 1 && "es"}</div>
      </div>

      {/* Issue #110: match history table (desktop/tablet, ≥ md) — unchanged. */}
      {renderDesktopTable(remove)}

      {/* Issue #110: match history cards (phone/small tablet, below md) —
          same data + same sort/filter state as the table above, presented
          as one card per match instead of a row that can't reflow. */}
      <div className="md:hidden space-y-3" data-testid="matches-cards">
        <div className="flex items-center gap-2">
          <Select value={sort.key} onValueChange={(v) => setSort((s) => ({ key: v, dir: s.dir }))}>
            <SelectTrigger className="h-11 flex-1" data-testid="perf-sort-mobile-key"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORT_COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-11 w-11 shrink-0"
            onClick={() => setSort((s) => ({ ...s, dir: s.dir === "asc" ? "desc" : "asc" }))}
            data-testid="perf-sort-mobile-dir"
            data-nav="in-place"
            title={sort.dir === "asc" ? "Ascending — tap to reverse" : "Descending — tap to reverse"}
          >
            {sort.dir === "asc" ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
          </Button>
        </div>

        {sorted.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="py-8 text-center text-slate-500 text-sm">{emptyLabel}</CardContent>
          </Card>
        )}
        {sorted.map((m) => <MatchCard key={m.match_id} m={m} openEdit={openEdit} remove={remove} />)}
      </div>
    </div>
  );
}

